import { useRef } from "react";
import type { PageContext } from "vike/types";
import { VikeReactProviderPageContext } from "vike-react/usePageContext";

/**
 * Freezes the vike-react pageContext for the lifetime of one page instance.
 *
 * The page transition (TransitionProvider) uses framer-motion's
 * `AnimatePresence mode="wait"`, which keeps the OUTGOING page mounted while it
 * animates out. But vike-react's data context (`usePageContext` / `useData`) is
 * a single React context provided at the app root, so the moment client-side
 * routing swaps pages, that context emits the INCOMING page's pageContext to
 * every consumer still mounted — including the outgoing page. The outgoing page
 * then re-renders against the next route's data shape (e.g. the shop index's
 * `products` is undefined on a `/shop/product/:id` route, so `products.length`
 * throws), which tears down the React tree and renders the 404 page.
 *
 * Capturing the pageContext once per instance and re-providing it here shadows
 * the root context for this subtree, so each page keeps reading its OWN data all
 * the way through its exit animation. A new route mounts a new instance (keyed
 * by pathname in TransitionProvider), which captures the new pageContext.
 */
export default function FrozenPageContext({
  pageContext,
  children,
}: {
  pageContext: PageContext;
  children: React.ReactNode;
}) {
  // useRef captures the value from this instance's first render and never
  // updates it — the freeze. AnimatePresence holds the outgoing instance, so it
  // never receives new props anyway; the ref is a belt-and-suspenders guarantee.
  const frozen = useRef(pageContext);
  return (
    <VikeReactProviderPageContext pageContext={frozen.current}>
      {children}
    </VikeReactProviderPageContext>
  );
}
